import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, Paper, Typography, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import { setShowInstallPrompt, setInstalled, setDeferredPrompt } from '../actions/pwa';

const InstallPWA = () => {
  const dispatch = useDispatch();
  const showInstallPrompt = useSelector((state) => state.pwa.showInstallPrompt);
  const deferredPrompt = useSelector((state) => state.pwa.deferredPrompt);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('PWA: App is already installed');
      dispatch(setInstalled(true));
      return;
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      console.log('PWA: Install prompt available');
      dispatch(setDeferredPrompt(e));

      // Show install prompt after 30 seconds (change to 3000 for testing)
      setTimeout(() => {
        dispatch(setShowInstallPrompt(true));
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Track installation
    window.addEventListener('appinstalled', () => {
      console.log('PWA: App was installed');
      dispatch(setInstalled(true));
      dispatch(setShowInstallPrompt(false));
      dispatch(setDeferredPrompt(null));
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [dispatch]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    console.log(`PWA: User ${outcome} the install prompt`);

    dispatch(setShowInstallPrompt(false));
    dispatch(setDeferredPrompt(null));
  };

  const handleDismiss = () => {
    dispatch(setShowInstallPrompt(false));
  };

  return (
    <Slide direction="up" in={showInstallPrompt} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          p: 2.5,
          zIndex: 1300,
          maxWidth: 450,
          mx: 'auto',
          borderTop: '4px solid #2089BE',
          backgroundColor: '#fff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              backgroundColor: '#022D41',
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <GetAppIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#022D41' }}>
              Install Airswift Digital
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
              Get quick access and work offline. Install our app for the best experience.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                size="medium"
                onClick={handleInstallClick}
                sx={{
                  backgroundColor: '#2089BE',
                  '&:hover': {
                    backgroundColor: '#022D41'
                  }
                }}
              >
                Install Now
              </Button>
              <Button
                variant="outlined"
                size="medium"
                onClick={handleDismiss}
                sx={{
                  borderColor: '#022D41',
                  color: '#022D41',
                  '&:hover': {
                    borderColor: '#2089BE',
                    backgroundColor: 'rgba(32, 137, 190, 0.04)'
                  }
                }}
              >
                Not Now
              </Button>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              mt: -1,
              mr: -1,
              color: '#022D41'
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Slide>
  );
};

export default InstallPWA;